import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { PefCell, PefFormatHint } from '../pef-schema.types';

export interface PefCellValueChange {
  valueKey: string;
  value: number | string | null;
}

const IT_LOCALE = 'it-IT';

function formatValue(
  value: number | string | null,
  formatHint: PefFormatHint | undefined,
): string {
  if (value === null || value === undefined || value === '') return '—';

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  switch (formatHint) {
    case 'currency':
      return new Intl.NumberFormat(IT_LOCALE, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true,
      }).format(num);
    case 'percentage':
      return new Intl.NumberFormat(IT_LOCALE, {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num / 100);
    case 'integer':
      return new Intl.NumberFormat(IT_LOCALE, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true,
      }).format(num);
    case 'decimal':
      return new Intl.NumberFormat(IT_LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
      }).format(num);
    default:
      return String(value);
  }
}

@Component({
  selector: 'lib-pef-cell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, InputNumberModule, InputTextModule, SkeletonModule],
  template: `
    @switch (cell().type) {
      @case ('input') {
        @if (readonly()) {
          <span
            class="pef-readonly-value"
            [class.pef-readonly-value--text]="cell().inputType === 'text'"
            >{{ readonlyDisplayValue() }}</span
          >
        } @else if (cell().inputType === 'text') {
          <input
            pInputText
            class="pef-input-text"
            [ngModel]="inputValue()"
            (ngModelChange)="onInputChange($event)"
          />
        } @else {
          <p-inputnumber
            class="pef-inputnumber"
            [autofocus]="false"
            [ngModel]="numericValue()"
            (ngModelChange)="onInputChange($event)"
            [mode]="inputNumberMode()"
            [currency]="inputNumberMode() === 'currency' ? 'EUR' : undefined"
            [locale]="locale"
            [suffix]="inputNumberSuffix()"
            [minFractionDigits]="inputNumberFractionDigits()"
            [maxFractionDigits]="inputNumberFractionDigits()"
            [showButtons]="false"
            [useGrouping]="true"
          />
        }
      }
      @case ('calculated') {
        @if (loading()) {
          <p-skeleton height="1rem" styleClass="pef-skeleton" />
        } @else {
          <span class="pef-calculated-value">{{ displayValue() }}</span>
        }
      }
      @case ('label') {
        <span class="pef-label-value">{{ cell().valueKey ?? '' }}</span>
      }
      @default {
        <!-- empty cell -->
      }
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .pef-calculated-value {
        display: block;
        text-align: right;
        white-space: nowrap;
      }
      .pef-readonly-value {
        display: block;
        padding: 2px 4px;
        font-size: 0.8125rem;
        text-align: right;
        white-space: nowrap;
      }
      .pef-readonly-value--text {
        text-align: left;
        white-space: normal;
      }
      .pef-label-value {
        display: block;
      }
      .pef-input-text {
        width: 100%;
        font-size: 0.8125rem;
        padding: 2px 4px;
        border: none;
        box-shadow: none;
        background: unset;
      }
      :host ::ng-deep .pef-inputnumber {
        width: 100%;
      }
      :host ::ng-deep .pef-inputnumber input {
        width: 100%;
        font-size: 0.8125rem;
        padding: 2px 4px;
        text-align: right;
        border: none;
        background: transparent;
        border-radius: 0;
        box-shadow: none;
      }
      :host ::ng-deep .pef-inputnumber input:focus {
        outline: 2px solid var(--p-primary-color);
        outline-offset: -2px;
      }
      :host ::ng-deep .pef-skeleton {
        margin: 2px 0;
      }
    `,
  ],
})
export class PefCellComponent {
  readonly cell = input.required<PefCell>();
  readonly value = input<number | string | null>(null);
  readonly loading = input<boolean>(false);
  /** Render input cells as plain text, formatted like a calculated one. */
  readonly readonly = input<boolean>(false);

  readonly valueChange = output<PefCellValueChange>();

  protected readonly locale = IT_LOCALE;

  /**
   * An empty text input reads as blank, not as the "—" a missing number gets:
   * a label nobody typed is not a value that is missing.
   */
  protected readonly readonlyDisplayValue = computed(() => {
    const value = this.value();
    if (this.cell().inputType === 'text')
      return value == null ? '' : String(value);
    return formatValue(value, this.cell().formatHint);
  });

  protected readonly displayValue = computed(() =>
    formatValue(this.value(), this.cell().formatHint),
  );

  protected readonly inputValue = computed(() => {
    const v = this.value();
    return v !== null && v !== undefined ? String(v) : '';
  });

  protected readonly numericValue = computed(() => {
    const v = this.value();
    if (v === null || v === undefined) return null;
    return typeof v === 'number' ? v : parseFloat(String(v)) || null;
  });

  protected readonly inputNumberMode = computed(() =>
    this.cell().inputType === 'currency' ? 'currency' : 'decimal',
  );

  protected readonly inputNumberSuffix = computed(() =>
    this.cell().inputType === 'percentage' ? ' %' : '',
  );

  protected readonly inputNumberFractionDigits = computed(() => {
    const t = this.cell().inputType;
    return t === 'currency' || t === 'percentage' || t === 'decimal' ? 2 : 0;
  });

  protected onInputChange(newValue: number | string | null): void {
    const key = this.cell().valueKey;
    if (!key) return;
    this.valueChange.emit({ valueKey: key, value: newValue });
  }
}
